import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { ArrowLeft, CalendarPlus, ExternalLink, MapPin, Clock, Users, Ticket, Newspaper, FileText, BarChart3, Star, Share2, Check } from "lucide-react";
import { useDarkMode } from "../hooks/useDarkMode";
import { useAuth } from "../AuthContext";
import PublicPageNav from "../components/PublicPageNav";
import PublicPageFooter from "../components/PublicPageFooter";
import EmailCapture from "../components/EmailCapture";
import { EventLogo } from "../components/events/EventLogo";
import { ProposeEventBlock } from "../components/events/ProposeEventBlock";
import { Chip } from "../components/events/EventCard";
import { BACKEND_URL } from "../lib/backend";
import { setAlternate, setCanonical, setDescription, setJsonLd, setNoIndex, SITE_URL } from "../lib/seo";
import { eventSeoDescription, fmtRange, hostOf, organizerLd } from "../lib/events";

function Block({ title, icon: Icon, children, c, testid }) {
  return (
    <section data-testid={testid} className={`rounded-2xl border p-5 ${c.card} ${c.border}`}>
      <h2 className={`category-tag text-xs mb-3 flex items-center gap-2 ${c.muted}`}>{Icon && <Icon className="w-3.5 h-3.5" />}{title}</h2>
      {children}
    </section>
  );
}

export default function EventDetailPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [dm] = useDarkMode();
  const { user } = useAuth() || {};
  const [data, setData] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [gone, setGone] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    setData(null); setNotFound(false); setGone(false);
    axios.get(`${BACKEND_URL}/api/events/by-slug/${slug}`).then(({ data }) => {
      if (data?.event?.slug && data.event.slug !== slug) { navigate(`/events/${data.event.slug}`, { replace: true }); return; } // merged duplicate → surviving URL
      setData(data);
    }).catch((e) => { setNotFound(true); setGone(e?.response?.status === 404); });
  }, [slug, navigate]);

  useEffect(() => (gone ? setNoIndex() : undefined), [gone]);

  useEffect(() => {
    if (!data) return;
    const ev = data.event;
    document.title = `${ev.name} — dates, venue & what to expect | TourismIntel Events`;
    const c1 = setDescription(ev.why_it_matters ? ev.why_it_matters.slice(0, 280) : eventSeoDescription(ev));
    setCanonical(data.canonical);
    const c3 = setAlternate("text/calendar", `${SITE_URL}/api/events/${ev.slug}.ics`);
    const where = [...new Set([ev.venue, ev.city, ev.country].filter(Boolean))].join(", ");
    const ld = {
      "@context": "https://schema.org", "@type": "Event", name: ev.name, url: data.canonical, startDate: ev.start_date, endDate: ev.end_date || ev.start_date,
      eventStatus: "https://schema.org/EventScheduled", eventAttendanceMode: "https://schema.org/OfflineEventAttendanceMode",
      description: ev.why_it_matters || eventSeoDescription(ev), image: `${SITE_URL}/api/og/event/${ev.slug}`,
      ...(ev.official_url ? { sameAs: ev.official_url } : {}),
      ...(organizerLd(ev) ? { organizer: organizerLd(ev) } : {}),
      location: { "@type": "Place", name: ev.venue || ev.city || where, address: { "@type": "PostalAddress", addressLocality: ev.city, addressCountry: ev.country_code || ev.country },
        ...(ev.lat != null ? { geo: { "@type": "GeoCoordinates", latitude: ev.lat, longitude: ev.lng } } : {}) },
      ...(ev.access === "free" ? { isAccessibleForFree: true } : {}),
    };
    const c4 = setJsonLd("event-jsonld", ld);
    const c5 = setJsonLd("event-breadcrumb-jsonld", { "@context": "https://schema.org", "@type": "BreadcrumbList", itemListElement: [
      { "@type": "ListItem", position: 1, name: "Industry events", item: `${SITE_URL}/events` },
      { "@type": "ListItem", position: 2, name: ev.name, item: data.canonical }] });
    return () => { c1(); c3(); c4(); c5(); };
  }, [data]);

  const c = dm
    ? { bg: "bg-[#0f1718]", card: "bg-[#1a2324]", border: "border-slate-700/50", text: "text-slate-200", muted: "text-slate-500", sub: "text-slate-300", accent: "text-cyan-400", btn: "bg-cyan-400 text-[#001f24] hover:bg-cyan-300", btn2: "bg-[#1a2324] text-slate-200 border border-slate-700/60 hover:bg-[#222d2e]", chip: "bg-white/6 text-slate-300" }
    : { bg: "bg-[#f5f6f7]", card: "bg-white", border: "border-[#e7e8e9]", text: "text-[#191c1d]", muted: "text-[#6b7a7d]", sub: "text-[#3b494c]", accent: "text-[#006874]", btn: "bg-[#006874] text-white hover:bg-[#004f58]", btn2: "bg-white text-[#191c1d] border border-[#e7e8e9] hover:bg-[#f3f4f5]", chip: "bg-[#f3f4f5] text-[#3b494c]" };

  const share = async () => {
    // same pattern as Reports: the share link is the bot-readable preview (OG tags), humans get redirected to the page
    try { await navigator.clipboard.writeText(`${BACKEND_URL}/api/preview/event/${data.event.slug}`); setCopied(true); setTimeout(() => setCopied(false), 1500); } catch { /* clipboard unavailable */ }
  };

  if (notFound) {
    return (
      <div className={`min-h-screen flex flex-col ${c.bg}`}><PublicPageNav />
        <main className="flex-1 max-w-3xl mx-auto w-full px-6 py-16 text-center" data-testid="event-not-found">
          <h1 className={`text-2xl font-semibold ${c.text}`}>Event not found</h1>
          <p className={`mt-2 text-sm ${c.muted}`}>It may have been removed or its dates are not confirmed yet.</p>
          <Link to="/events" className={`inline-block mt-6 text-sm font-semibold ${c.accent}`}>← Back to the industry calendar</Link>
        </main><PublicPageFooter /></div>
    );
  }
  if (!data) {
    return <div className={`min-h-screen flex flex-col ${c.bg}`}><PublicPageNav /><main className="flex-1 flex items-center justify-center"><div className={`w-6 h-6 rounded-full border-2 border-t-transparent animate-spin ${dm ? "border-cyan-400" : "border-[#006874]"}`} /></main></div>;
  }

  const ev = data.event;
  const rel = data.related || {};
  const where = [...new Set([ev.venue, ev.city, ev.country].filter(Boolean))].join(", ");

  return (
    <div className={`min-h-screen flex flex-col ${c.bg}`}>
      <PublicPageNav />
      <main className="flex-1 max-w-5xl mx-auto w-full px-4 sm:px-6 py-10" data-testid="event-detail-page">
        <nav className="mb-6 text-xs" data-testid="event-breadcrumb">
          <Link to="/events" className={`inline-flex items-center gap-1 ${c.muted} hover:underline`}><ArrowLeft className="w-3 h-3" /> Industry events</Link>
          {ev.region_label && <span className={c.muted}> · <Link to={`/events?region=${ev.region}`} className="hover:underline">{ev.region_label}</Link></span>}
        </nav>

        <header className="flex items-start gap-5 mb-8">
          <EventLogo event={ev} dm={dm} size={72} />
          <div className="min-w-0 flex-1">
            <p className={`text-xs font-semibold ${c.accent}`} data-testid="event-organiser">{ev.organiser || hostOf(ev.official_url)}</p>
            <h1 className={`editorial-heading text-3xl sm:text-4xl font-semibold leading-tight ${c.text}`} data-testid="event-title">{ev.name}</h1>
            <div className="mt-3 flex items-center gap-1.5 flex-wrap">
              <Chip className={c.chip} testid="event-type-chip">{ev.event_type_label}</Chip>
              {ev.format_label && <Chip className={c.chip}>{ev.format_label}</Chip>}
              {ev.theme_labels.map((t) => <Chip key={t} className={c.chip}>{t}</Chip>)}
              {ev.ongoing && <Chip className="bg-emerald-500/15 text-emerald-500">Happening now</Chip>}
              {ev.covered_by_ti && <Chip className={dm ? "bg-cyan-400/15 text-cyan-300" : "bg-[#006874]/10 text-[#006874]"} testid="event-covered-badge"><Star className="w-2.5 h-2.5" /> Covered by TourismIntel</Chip>}
            </div>
          </div>
        </header>

        <div className="grid gap-5 lg:grid-cols-[1fr_320px]">
          <div className="space-y-5">
            <section data-testid="event-facts" className={`rounded-2xl border p-5 grid sm:grid-cols-2 gap-4 ${c.card} ${c.border}`}>
              <div>
                <p className={`category-tag text-[10px] ${c.muted}`}>WHEN</p>
                <p className={`mt-1 text-base font-semibold tabular-nums ${c.text}`} data-testid="event-when">{fmtRange(ev.start_date, ev.end_date)}</p>
                {ev.edition && <p className={`text-xs ${c.muted}`}>{ev.edition}</p>}
              </div>
              <div>
                <p className={`category-tag text-[10px] ${c.muted}`}>WHERE</p>
                <p className={`mt-1 text-base font-semibold ${c.text} flex items-start gap-1`} data-testid="event-where"><MapPin className="w-4 h-4 mt-0.5 flex-shrink-0" />{where || "To be announced"}</p>
              </div>
              <div>
                <p className={`category-tag text-[10px] ${c.muted}`}>ACCESS</p>
                <p className={`mt-1 text-sm font-semibold ${c.text} flex items-center gap-1`} data-testid="event-access"><Ticket className="w-3.5 h-3.5" />{ev.access_label}</p>
                {ev.closes_in_days !== null && ev.closes_in_days !== undefined && (
                  <p className="text-xs text-rose-500 font-semibold flex items-center gap-1 mt-0.5" data-testid="event-closing"><Clock className="w-3 h-3" /> Registration closes in {ev.closes_in_days} day{ev.closes_in_days !== 1 ? "s" : ""} ({ev.registration_deadline})</p>
                )}
              </div>
              <div>
                <p className={`category-tag text-[10px] ${c.muted}`}>ATTENDANCE</p>
                {ev.attendance?.value ? (
                  <p className={`mt-1 text-sm font-semibold ${c.text} flex items-center gap-1`} data-testid="event-attendance"><Users className="w-3.5 h-3.5" />{ev.attendance.value}
                    <span className={`text-[10px] font-normal ${c.muted}`}>organiser-reported{ev.attendance.year ? `, ${ev.attendance.year}` : ""}</span></p>
                ) : <p className={`mt-1 text-sm ${c.muted}`}>Not published by the organiser</p>}
              </div>
            </section>

            {ev.why_it_matters && (
              <Block title="WHY IT MATTERS" c={c} testid="event-why">
                <p className={`text-[15px] leading-relaxed ${c.sub}`}>{ev.why_it_matters}</p>
                <p className={`mt-3 text-[11px] ${c.muted}`}>Editorial note by TourismIntel, written from the organiser&apos;s official material. Figures only when published by the organiser.</p>
              </Block>
            )}

            {(rel.news?.length > 0 || rel.reports?.length > 0 || rel.pulse?.url) && (
              <Block title="TOURISMINTEL INTELLIGENCE" c={c} testid="event-intel">
                <div className="grid sm:grid-cols-2 gap-4">
                  {rel.news?.length > 0 && (
                    <div data-testid="event-related-news">
                      <p className={`text-xs font-semibold mb-2 flex items-center gap-1 ${c.text}`}><Newspaper className="w-3.5 h-3.5" /> In the news</p>
                      <ul className="space-y-1.5">{rel.news.map((n) => (
                        <li key={n.id} className="text-xs"><a href={n.url} target="_blank" rel="noopener noreferrer" className={`hover:underline ${c.sub}`}>{n.title}</a><span className={c.muted}> · {n.source}</span></li>))}</ul>
                    </div>
                  )}
                  {rel.reports?.length > 0 && (
                    <div data-testid="event-related-reports">
                      <p className={`text-xs font-semibold mb-2 flex items-center gap-1 ${c.text}`}><FileText className="w-3.5 h-3.5" /> Reports by {rel.reports[0].organisation}</p>
                      <ul className="space-y-1.5">{rel.reports.map((r) => (
                        <li key={r.slug} className="text-xs"><Link to={r.brief_url} className={`hover:underline ${c.sub}`}>{r.title}</Link></li>))}</ul>
                    </div>
                  )}
                  {rel.pulse?.url && (
                    <div data-testid="event-related-pulse">
                      <p className={`text-xs font-semibold mb-2 flex items-center gap-1 ${c.text}`}><BarChart3 className="w-3.5 h-3.5" /> Host country data</p>
                      <Link to={rel.pulse.url} className={`text-xs hover:underline ${c.accent}`}>{rel.pulse.label} →</Link>
                    </div>
                  )}
                </div>
              </Block>
            )}

            {rel.editions?.length > 0 && (
              <Block title="OTHER EDITIONS" c={c} testid="event-editions">
                <ul className="space-y-1.5">{rel.editions.map((e) => (
                  <li key={e.id} className="text-sm"><Link to={`/events/${e.slug}`} className={`hover:underline ${c.sub}`} data-testid={`event-edition-${e.id}`}>{e.name}</Link><span className={`text-xs ${c.muted}`}> · {fmtRange(e.start_date, e.end_date)}{e.city ? ` · ${e.city}` : ""}</span></li>))}</ul>
              </Block>
            )}

            {data.same_week?.length > 0 && (
              <Block title="THE SAME WEEK" c={c} testid="event-same-week">
                <ul className="space-y-1.5">{data.same_week.map((e) => (
                  <li key={e.id} className="text-sm"><Link to={`/events/${e.slug}`} className={`hover:underline ${c.sub}`}>{e.name}</Link><span className={`text-xs ${c.muted}`}> · {fmtRange(e.start_date, e.end_date)} · {[e.city, e.country].filter(Boolean).join(", ")}</span></li>))}</ul>
              </Block>
            )}
            <ProposeEventBlock dm={dm} compact />
          </div>

          <aside className="space-y-4">
            <div className={`rounded-2xl border p-5 ${c.card} ${c.border}`} data-testid="event-actions">
              {ev.official_url && (
                <a href={ev.official_url} target="_blank" rel="noopener noreferrer" data-testid="event-official-link"
                  className={`flex items-center justify-center gap-2 w-full px-4 py-2.5 rounded-full text-sm font-semibold transition-colors ${c.btn}`}>
                  Official website <ExternalLink className="w-3.5 h-3.5" />
                </a>
              )}
              {ev.registration_url && (
                <a href={ev.registration_url} target="_blank" rel="noopener noreferrer" data-testid="event-registration-link"
                  className={`mt-2 flex items-center justify-center gap-2 w-full px-4 py-2.5 rounded-full text-sm font-semibold ${c.btn2}`}>
                  <Ticket className="w-3.5 h-3.5" /> Registration
                </a>
              )}
              <a href={`${BACKEND_URL}/api/events/${ev.slug}.ics`} data-testid="event-add-to-calendar"
                className={`mt-2 flex items-center justify-center gap-2 w-full px-4 py-2.5 rounded-full text-sm font-semibold ${c.btn2}`}>
                <CalendarPlus className="w-3.5 h-3.5" /> Add to calendar (.ics)
              </a>
              <button onClick={share} data-testid="event-share-copy" className={`mt-2 flex items-center justify-center gap-2 w-full px-4 py-2.5 rounded-full text-sm font-semibold ${c.btn2}`}>
                {copied ? <Check className="w-3.5 h-3.5" /> : <Share2 className="w-3.5 h-3.5" />}{copied ? "Link copied" : "Copy link"}
              </button>
              <p className={`mt-4 text-[11px] leading-relaxed ${c.muted}`} data-testid="event-date-source">
                Dates {ev.date_source === "organizer_page" || ev.date_source === "curated" || ev.date_source === "admin" ? "confirmed on the organiser's official website" : "from the organiser's official announcement"}. Always check the official site before booking.
              </p>
            </div>
            {!user && (
            <div className={`rounded-2xl border p-5 ${c.card} ${c.border}`} data-testid="event-signup-cta">
              <p className={`text-sm font-semibold ${c.text}`}>Never miss a deadline</p>
              <p className={`mt-1 text-xs ${c.muted}`}>Weekly Travel Brief with upcoming events. Personal planner and .ics reminders coming for members.</p>
              <div className="mt-3"><EmailCapture darkMode={dm} source="event_page" /></div>
            </div>
            )}
          </aside>
        </div>
      </main>
      <PublicPageFooter />
    </div>
  );
}
